import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { tap, filter, take, switchMap, retry } from 'rxjs/operators';
import { ErrorHandlerService } from '@app/core/error-handler/error-handler.service';
import { AuthenticationService } from '@app/core/services/authentication/authentication.service';
import { Store } from '@ngrx/store';
import { AppState } from '@app/store/store.states';
import { LogOut } from '@app/store/auth/auth.actions';
import { AuthTokenService } from '@app/core/services/auth-token/auth-token.service';
import { defaultDialogConfig } from '@app/shared/configs/default.dialog.config';
import { MatDialog } from '@angular/material/dialog';
import { AssistedModeDialogComponent } from '@app/shared/modals/assisted-mode-dialog/assisted-mode-dialog.component';
import { FeedbackDialogComponent } from '@app/theme/feedback-dialog/feedback-dialog.component';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;

  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private UNAUTHORIZED_API_STATUS = 401;

  private DEFAULT_REQUEST_RETRY_ATTEMPTS_NUMBER = 1;

  constructor(
    private readonly injector: Injector,
    private readonly authenticationService: AuthenticationService,
    private readonly store: Store<AppState>,
    private readonly authTokenService: AuthTokenService,
    public dialog: MatDialog
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retry(this.DEFAULT_REQUEST_RETRY_ATTEMPTS_NUMBER),
      tap({
        error: (error: any) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === this.UNAUTHORIZED_API_STATUS) {
              if (error.error.message === 'Os sistemas estão em manutenção.') {
                const applicationErrorHandler = this.injector.get(ErrorHandlerService);
                applicationErrorHandler.handleError(error);
                return this.dialog.open(FeedbackDialogComponent, {
                  data: {
                    data: {
                      color: 'error',
                      message: error.error.message,
                      showCloseButton: false,
                    },
                  },
                  panelClass: 'feedback-modalbox',
                  ...defaultDialogConfig,
                  disableClose: true,
                });
              } else if (this.refreshTokenInProgress) {
                return this.refreshTokenSubject.pipe(
                  filter((result) => result !== null),
                  take(1),
                  switchMap(() =>
                    next.handle(this.addRequestAuthenticationToken(request))
                  )
                );
              } else {
                this.refreshTokenInProgress = true;

                this.refreshTokenSubject.next(null);

                return this.authenticationService.refreshAccessToken().subscribe(
                  (token: any) => {
                    this.refreshTokenInProgress = false;

                    this.refreshTokenSubject.next(token);

                    return next.handle(this.addRequestAuthenticationToken(request));
                  },
                  (err: Error) => {
                    this.store.dispatch(new LogOut());

                    return throwError(err);
                  }
                );
              }
            } else if (error.status === 403) {
              // MODAL OK?
              this.openDialog(AssistedModeDialogComponent, {});
            }

            const applicationErrorHandler = this.injector.get(ErrorHandlerService);

            applicationErrorHandler.handleError(error);
          }
        },
      })
    );
  }

  addRequestAuthenticationToken(request: HttpRequest<any>) {
    const accessToken = this.authTokenService.getToken();

    if (accessToken) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    }

    return request;
  }

  openDialog(dialogComponent, data = {}) {
    return this.dialog.open(dialogComponent, {
      data,
      ...defaultDialogConfig,
      disableClose: true,
    });
  }
}
